import { graphql } from "gatsby"
import * as React from "react"
import List from "../components/guides/List"
import Layout from "../components/layout/Layout"
import { splitGuides } from "../components/utils/Guide"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleDown, faSearch } from "@fortawesome/free-solid-svg-icons"
import { DefaultBottom } from "../components/banner/DefaultBottom"
import Fuse from "fuse.js"
import { HTMLContent } from "../components/common/Content"

// props
interface Props {
  data: {
    page: {
      html: any
      frontmatter: {
        title: string
        description?: string
        keywords?: Array<string>
        guides: string
        topicalGuides: string
        tutorials: string
      }
    }
    guides: {
      nodes: Array<{
        description: string
        name: string
        path: string
        title: string
        type: string
        categeory: Array<string>
      }>
    }
  }
}

const onlyUnique = (value: any, index: number, array: Array<any>) => {
  return array.indexOf(value) === index
}

const flattenCategories = (guides: Array<any>) => {
  return guides
    .map((guide: any) => guide.category)
    .flat()
    .filter(onlyUnique)
    .sort((a: any, b: any) => {
      if (b === "Misc") {
        return -1
      }
      return 0
    })
    .map((category: any) => ({
      label: category,
      value: false,
    }))
}

const Category = ({ label, value, onChange }: any) => {
  return (
    <div>
      <label className="b-checkbox checkbox is-small">
        <input
          type="checkbox"
          value="false"
          onChange={() => onChange(!value)}
        />
        <span className="check is-info"></span>
        <span className="control-label">{label}</span>
      </label>
    </div>
  )
}

// markup
const GuidesTemplate = (props: Props) => {
  const [search, setSearch] = React.useState<any>()
  const [query, setQuery] = React.useState("")
  const [result, setResult] = React.useState<any>([])
  const [categories, setCategories] = React.useState<any>(
    flattenCategories(props.data.guides.nodes)
  )

  const [times, setTimes] = React.useState<any>([
    {
      label: "30mn",
      value: false,
    },
    {
      label: "1h",
      value: false,
    },
    {
      label: "3h",
      value: false,
    },
  ])

  React.useEffect(() => {
    const options = {
      includeScore: true,
      threshold: 0.3,
      ignoreLocation: true,
      findAllMatches: true,
      keys: [
        { name: "title", weight: 1 },
        { name: "description", weight: 1 },
      ],
    }
    const fuse = new Fuse(props.data.guides.nodes, options)
    setSearch(fuse)
  }, [props.data.guides.nodes])

  React.useEffect(() => {
    const onSearch = () => {
      if (!search) {
        return
      }
      let vals = props.data.guides.nodes
      if (query.trim()) {
        vals = search.search(query).map((val: any) => val.item)
      }
      const searchTime = times.filter((time: any) => time.value)
      if (searchTime.length) {
        vals = vals.filter((val: any) => {
          return searchTime.some((time: any) => {
            if (time.label === "30mn") {
              return val.type === "getting-started"
            }
            if (time.label === "1h") {
              return val.type === "topical"
            }
            if (time.label === "3h") {
              return val.type === "tutorial"
            }
            return false
          })
        })
      }

      const searchCategory = categories.filter((cat: any) => cat.value)
      if (searchCategory.length) {
        vals = vals.filter((val: any) => {
          return searchCategory.some((cat: any) => {
            return val.category.includes(cat.label)
          })
        })
      }
      setResult(vals)
    }
    onSearch()
  }, [query, search, props.data.guides.nodes, categories, times])

  return (
    <Layout
      seo={{
        title: props.data.page.frontmatter.title,
        description: props.data.page.frontmatter.description,
        keywords: props.data.page.frontmatter.keywords,
      }}
      className="guides"
    >
      <div className="has-background-light">
        <div className="container">
          <div className="hero hero-guides">
            <div className="content">
              <h1 className="is-size-1">Guides</h1>
              <div className="description">
                <HTMLContent content={props.data.page.html} />
              </div>
              <div className="image">
                <img className="img-light" src="/img/guides.svg" alt="" />
                <img className="img-dark" src="/img/guides-dark.svg" alt="" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="extra-0">
          <img src={`/img/extra/microservices-4.svg`} alt="" />
        </div>

        <div className="container pb-6 pt-4">
          <div className="search-sticky">
            <div className="py-5">
              <div className="search">
                <input
                  className="input is-special"
                  placeholder="Find a guide"
                  value={query}
                  onChange={event => {
                    setQuery(event.target.value)
                  }}
                  autoComplete="off"
                />
                <FontAwesomeIcon className="icon" icon={faSearch} />
              </div>
            </div>
            <div className="columns my-0 mx-0">
              <div className="column is-2 px-0 py-0 my-0 mx-0"></div>
              <div className="column is-10 px-0 py-0 my-0 mx-0">
                <div className="has-border-bottom mx-4"></div>
              </div>
            </div>
          </div>
          <div className="columns">
            <div className="column is-2 pr-0 has-border-right">
              <div className="is-sticky">
                <div>
                  <div className="pb-2">
                    <h1 className="has-text-weight-bold">Category</h1>
                  </div>
                  <div className="">
                    {categories.map((category: any, index: number) => (
                      <Category
                        key={`category-${index}`}
                        label={`${category.label}`}
                        value={category.value}
                        onChange={(value: boolean) => {
                          const args = categories.map((category: any) => ({
                            ...category,
                          }))
                          args[index] = {
                            label: args[index].label,
                            value: value,
                          }
                          setCategories(args)
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div className="pt-5">
                  <div className="pb-2">
                    <h1 className="has-text-weight-bold">Estimated Time</h1>
                  </div>
                  <div className="">
                    {times.map((time: any, index: number) => (
                      <Category
                        key={`time-${index}`}
                        label={`${time.label}`}
                        value={time.value}
                        onChange={(value: boolean) => {
                          const args = times.map((time: any) => ({ ...time }))
                          args[index] = {
                            label: args[index].label,
                            value: value,
                          }
                          setTimes(args)
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="column is-10 pt-5 mt-3 px-5">
              {result?.length > 0 ? (
                <List items={result} />
              ) : (
                <div className="pt-6 pb-6 has-text-centered has-text-weight-bold">
                  No result found.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <DefaultBottom />
    </Layout>
  )
}

export default GuidesTemplate

// graphql queries
export const pageQuery = graphql`
  query GuidesTemplate($id: String!) {
    guides: allApiGuide {
      nodes {
        description
        name
        title
        type
        path
        category
      }
    }
    page: markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        title
        description
        keywords
        guides
        topicalGuides
        tutorials
      }
    }
  }
`
